import React from "react";
import { Button, Form, InputGroup } from "react-bootstrap";
import { REPORT_TOOLBAR_CLASS } from "@core/reports/constants/reportToolbar";
import type { ReportSectionView, ReportSectionViewLensParams } from "@core/reports/utils/reportSectionViewLens";
import type { ReportFormSectionViewMeta } from "@core/reports/api/types";
import {
  REPORT_SECTION_VIEWS,
  currentUtcMonth,
  currentUtcSunday,
  utcMonthBounds,
  viewLabelForSection,
  weekOfLabel,
} from "@core/reports/utils/reportSectionViewLens";

type Props = {
  sectionType: string;
  lens: ReportSectionViewLensParams;
  viewMeta?: ReportFormSectionViewMeta | null;
  /** Views to leave out of the picker, e.g. LAYOUT_EXCLUDED_VIEWS beside a named layout. */
  excludeViews?: readonly ReportSectionView[];
  onChange: (next: ReportSectionViewLensParams) => void;
};

function toDatetimeLocalValue(value?: string): string {
  return value?.replace(" ", "T").slice(0, 16) ?? "";
}

function fromDatetimeLocalValue(value: string): string | undefined {
  return value ? `${value.replace("T", " ")}:00` : undefined;
}

const ReportSectionViewToolbar: React.FC<Props> = ({
  sectionType,
  lens,
  viewMeta,
  excludeViews = [],
  onChange,
}) => {
  const viewId = `report-section-view-${sectionType}`;
  const monthId = `report-section-month-${sectionType}`;
  const weekId = `report-section-week-${sectionType}`;

  const hasDeadline = Boolean(viewMeta?.deadline);
  const available = new Set(
    (viewMeta?.available_views?.length
      ? viewMeta.available_views
      : REPORT_SECTION_VIEWS.filter((v) => hasDeadline || (v !== "on_time" && v !== "late"))
    )
      .map((v) => v.toLowerCase())
      .filter((v) => !excludeViews.includes(v as ReportSectionView))
  );

  const monthOptions =
    viewMeta?.bucket_options?.filter((o) => o.type === "month").map((o) => o.value) ?? [];
  const weekOptions =
    viewMeta?.bucket_options?.filter((o) => o.type === "week") ?? [];

  const [draftFrom, setDraftFrom] = React.useState(lens.from);
  const [draftTo, setDraftTo] = React.useState(lens.to);
  const healedCustomWindowRef = React.useRef(false);

  React.useEffect(() => {
    setDraftFrom(lens.from);
    setDraftTo(lens.to);
  }, [lens.from, lens.to]);

  React.useEffect(() => {
    if (healedCustomWindowRef.current) return;
    if (lens.view !== "custom_window") return;
    if (lens.from && lens.to) {
      healedCustomWindowRef.current = true;
      return;
    }
    healedCustomWindowRef.current = true;
    const bounds = utcMonthBounds();
    onChange({
      view: "custom_window",
      from: lens.from || bounds.from,
      to: lens.to || bounds.to,
    });
  }, [lens.view, lens.from, lens.to, onChange]);

  const applyCustomWindow = () => {
    if (!draftFrom || !draftTo) return;
    if (draftFrom > draftTo) return;
    if (draftFrom === lens.from && draftTo === lens.to) return;
    onChange({ view: "custom_window", from: draftFrom, to: draftTo });
  };

  const handleViewChange = (view: ReportSectionView) => {
    const next: ReportSectionViewLensParams = { view };
    if (view === "by_month") {
      next.bucket =
        lens.view === "by_month" && lens.bucket
          ? lens.bucket
          : monthOptions[monthOptions.length - 1] ?? currentUtcMonth();
    } else if (view === "by_week") {
      next.bucket =
        lens.view === "by_week" && lens.bucket
          ? lens.bucket
          : weekOptions[weekOptions.length - 1]?.value ?? currentUtcSunday();
    } else if (view === "custom_window") {
      const bounds = utcMonthBounds();
      next.from = lens.from || bounds.from;
      next.to = lens.to || bounds.to;
      setDraftFrom(next.from);
      setDraftTo(next.to);
    }
    onChange(next);
  };

  const customWindowIncomplete = lens.view === "custom_window" && (!draftFrom || !draftTo);
  const customWindowInverted =
    lens.view === "custom_window" && Boolean(draftFrom && draftTo && draftFrom > draftTo);
  const customWindowUnchanged =
    lens.view === "custom_window" && draftFrom === lens.from && draftTo === lens.to;
  const applyDisabled =
    customWindowIncomplete || customWindowInverted || customWindowUnchanged;

  return (
    <>
      <Form.Group
        className={`mb-0 d-flex align-items-center gap-2 ${REPORT_TOOLBAR_CLASS.control} ${REPORT_TOOLBAR_CLASS.view}`}
        data-testid={`report-section-view-toolbar-${sectionType}`}
      >
        <Form.Label htmlFor={viewId} className="small text-muted mb-0">
          View
        </Form.Label>
        <Form.Select
          id={viewId}
          size="sm"
          value={lens.view}
          onChange={(e) => handleViewChange(e.target.value as ReportSectionView)}
          aria-label="Section view mode"
        >
          {REPORT_SECTION_VIEWS.filter((v) => available.has(v)).map((v) => (
            <option key={v} value={v} disabled={(v === "on_time" || v === "late") && !hasDeadline}>
              {viewLabelForSection(v, sectionType)}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      {lens.view === "by_month" ? (
        <Form.Group className={`mb-0 d-flex align-items-center gap-2 ${REPORT_TOOLBAR_CLASS.control}`}>
          <Form.Label htmlFor={monthId} className="small text-muted mb-0">
            Month (UTC)
          </Form.Label>
          <Form.Control
            id={monthId}
            size="sm"
            type="month"
            value={lens.bucket ?? currentUtcMonth()}
            onChange={(e) => onChange({ ...lens, bucket: e.target.value })}
            aria-label="Month (UTC)"
          />
        </Form.Group>
      ) : null}

      {lens.view === "by_week" ? (
        <Form.Group className={`mb-0 d-flex align-items-center gap-2 ${REPORT_TOOLBAR_CLASS.control} ${REPORT_TOOLBAR_CLASS.week}`}>
          <Form.Label htmlFor={weekId} className="small text-muted mb-0">
            Week (Sun–Sat UTC)
          </Form.Label>
          <Form.Select
            id={weekId}
            size="sm"
            value={lens.bucket ?? currentUtcSunday()}
            onChange={(e) => onChange({ ...lens, bucket: e.target.value })}
            aria-label="Week (Sun–Sat UTC)"
          >
            {(weekOptions.length
              ? weekOptions
              : [{ value: currentUtcSunday(), label: weekOfLabel(currentUtcSunday()) }]
            ).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label ?? weekOfLabel(o.value)}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      ) : null}

      {lens.view === "custom_window" ? (
        <InputGroup size="sm" className={REPORT_TOOLBAR_CLASS.custom}>
          <InputGroup.Text>From</InputGroup.Text>
          <Form.Control
            type="datetime-local"
            value={toDatetimeLocalValue(draftFrom)}
            onChange={(e) => setDraftFrom(fromDatetimeLocalValue(e.target.value))}
            aria-label="Custom window from"
          />
          <InputGroup.Text>To</InputGroup.Text>
          <Form.Control
            type="datetime-local"
            value={toDatetimeLocalValue(draftTo)}
            onChange={(e) => setDraftTo(fromDatetimeLocalValue(e.target.value))}
            aria-label="Custom window to"
          />
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={applyDisabled}
            onClick={applyCustomWindow}
            aria-label="Apply custom window"
          >
            Apply
          </Button>
        </InputGroup>
      ) : null}

      {customWindowIncomplete ? (
        <span className={`small text-muted ${REPORT_TOOLBAR_CLASS.hint}`}>Choose both From and To to apply this view.</span>
      ) : null}
      {customWindowInverted ? (
        <span className={`small text-muted ${REPORT_TOOLBAR_CLASS.hint}`}>From must be on or before To.</span>
      ) : null}

      {!hasDeadline && (lens.view === "on_time" || lens.view === "late") ? (
        <span className={`small text-muted ${REPORT_TOOLBAR_CLASS.hint}`}>No deadline set for this period.</span>
      ) : null}
    </>
  );
};

export default ReportSectionViewToolbar;
